import Navbar from "../components/Navbar/Navbar";
import Filters from "../components/Filters/Filters";
import ChartTabs from "../components/Charts/ChartTabs";
import SummaryBar from "../components/SummaryBar/SummaryBar";
import ChargerMap from "../components/Map/ChargerMap";
import Footer from "../components/Footer/Footer";
import { useEffect, useState } from "react";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:5000"
).replace(/\/$/, "");

function Dashboard() {
  const [locations, setLocations] = useState([]);
  const [chargerType, setChargerType] = useState("Both");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [revOnly, setRevOnly] = useState(false);
  const [locationsLoaded, setLocationsLoaded] = useState(false);

  const today = new Date();

  const firstDayOfMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );

  const lastDayOfMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  );

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(formatDate(firstDayOfMonth));
  const [endDate, setEndDate] = useState(formatDate(lastDayOfMonth));
  const [quickRange, setQuickRange] = useState("");
  const [grouping, setGrouping] = useState("daily");

  const [chargesPerDayData, setChargesPerDayData] = useState([]);

  const acData = chargesPerDayData.filter(
    (row) => row.chargerType === "AC"
  );

  const dcData = chargesPerDayData.filter(
    (row) => row.chargerType === "DC"
  );

  const [averageDayOfWeekData, setAverageDayOfWeekData] = useState([]);

  const acDayOfWeekData = averageDayOfWeekData.filter(
    (row) => row.chargerType === "AC"
  );

  const dcDayOfWeekData = averageDayOfWeekData.filter(
    (row) => row.chargerType === "DC"
  );

  const [energyBoxplotData, setEnergyBoxplotData] = useState([]);
  const [averageEnergyData, setAverageEnergyData] = useState([]);
  const [timeBoxplotData, setTimeBoxplotData] = useState([]);
  const [averageTimeData, setAverageTimeData] = useState([]);
  const [totalEnergyData, setTotalEnergyData] = useState([]);

  const acTotalEnergyData = totalEnergyData.filter(
    (row) => row.chargerType === "AC"
  );

  const dcTotalEnergyData = totalEnergyData.filter(
    (row) => row.chargerType === "DC"
  );

  const [averageEnergyByHourData, setAverageEnergyByHourData] = useState([]);
  const [utilizationByHourData, setUtilizationByHourData] = useState([]);
  const [stationSummaryData, setStationSummaryData] = useState([]);

  const [isMapOpen, setIsMapOpen] = useState(false);

  const [summaryData, setSummaryData] = useState({
    acUsed: 0,
    acTotal: 0,
    dcUsed: 0,
    dcTotal: 0
  });

  const moveMonth = (offset) => {
    let baseDate;

    if (startDate) {
      const [year, month] = startDate.split("-").map(Number);
      baseDate = new Date(year, month - 1, 1);
    } else {
      baseDate = new Date();
    }

    if (Number.isNaN(baseDate.getTime())) {
      return;
    }

    const targetStart = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth() + offset,
      1
    );

    const targetEnd = new Date(
      targetStart.getFullYear(),
      targetStart.getMonth() + 1,
      0
    );

    setQuickRange("");
    setStartDate(formatDate(targetStart));
    setEndDate(formatDate(targetEnd));
  };

  const handlePreviousMonth = () => {
    moveMonth(-1);
  };

  const handleNextMonth = () => {
    moveMonth(1);
  };

  // Refetch the available locations whenever REV-only changes.
  useEffect(() => {
    setLocationsLoaded(false);

    const params = new URLSearchParams();
    params.append("revOnly", revOnly ? "true" : "false");

    fetch(`${API_BASE_URL}/locations?${params.toString()}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Locations request failed with ${res.status}`
          );
        }

        return res.json();
      })
      .then((data) => {
        const locationData = Array.isArray(data) ? data : [];

        setLocations(locationData);

        const availableIds = [
          ...new Set(
            locationData.map(
              (location) => location.chargingStationId
            )
          )
        ];

        // Keep existing selections and select any newly visible locations.
        setSelectedLocations((current) => [
          ...new Set([
            ...current,
            ...availableIds
          ])
        ]);

        setLocationsLoaded(true);
      })
      .catch((err) => {
        console.error("Error fetching locations:", err);
        setLocations([]);
        setLocationsLoaded(true);
      });
  }, [revOnly]);

  // AC/DC filtering is applied on top of the REV location filter.
  const filteredLocations = locations.filter((location) => {
    if (chargerType === "Both") {
      return true;
    }

    return location.type === chargerType;
  });

  useEffect(() => {
    if (
      locationsLoaded &&
      selectedLocations.length === 0
    ) {
      setChargesPerDayData([]);
      setAverageDayOfWeekData([]);
      setEnergyBoxplotData([]);
      setAverageEnergyData([]);
      setTimeBoxplotData([]);
      setAverageTimeData([]);
      setTotalEnergyData([]);
      setAverageEnergyByHourData([]);
      setUtilizationByHourData([]);
      setStationSummaryData([]);

      setSummaryData({
        acUsed: 0,
        acTotal: 0,
        dcUsed: 0,
        dcTotal: 0
      });

      return;
    }

    const params = new URLSearchParams();

    params.append("chargerType", chargerType);
    params.append("grouping", grouping);
    params.append("revOnly", revOnly ? "true" : "false");

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    selectedLocations.forEach((locationId) => {
      params.append("locations", locationId);
    });

    const queryString = params.toString();

    fetch(`${API_BASE_URL}/charts/charges-over-time?${queryString}`)
      .then((res) => res.json())
      .then((data) => {
        setChargesPerDayData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching charges per day:", err);
        setChargesPerDayData([]);
      });

    fetch(`${API_BASE_URL}/charts/average-charges-day-of-week?${queryString}`)
      .then((res) => res.json())
      .then((data) => {
        setAverageDayOfWeekData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching day of week data:", err);
        setAverageDayOfWeekData([]);
      });

    fetch(`${API_BASE_URL}/charts/energy-boxplot?${queryString}`)
      .then((res) => res.json())
      .then((data) => {
        setEnergyBoxplotData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching energy boxplot:", err);
        setEnergyBoxplotData([]);
      });

    fetch(`${API_BASE_URL}/charts/average-energy-over-time?${queryString}`)
      .then((res) => res.json())
      .then((data) => {
        setAverageEnergyData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching average energy:", err);
        setAverageEnergyData([]);
      });

    fetch(`${API_BASE_URL}/charts/time-boxplot?${queryString}`)
      .then((res) => res.json())
      .then((data) => {
        setTimeBoxplotData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching time boxplot:", err);
        setTimeBoxplotData([]);
      });

    fetch(`${API_BASE_URL}/charts/average-time-over-time?${queryString}`)
      .then((res) => res.json())
      .then((data) => {
        setAverageTimeData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching average time:", err);
        setAverageTimeData([]);
      });

    fetch(`${API_BASE_URL}/charts/total-energy-over-time?${queryString}`)
      .then((res) => res.json())
      .then((data) => {
        setTotalEnergyData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching total energy:", err);
        setTotalEnergyData([]);
      });

    fetch(`${API_BASE_URL}/charts/average-energy-by-hour?${queryString}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Average energy by hour request failed with ${res.status}`
          );
        }

        return res.json();
      })
      .then((data) => {
        setAverageEnergyByHourData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching average energy by hour:", err);
        setAverageEnergyByHourData([]);
      });

    fetch(`${API_BASE_URL}/charts/utilization-by-hour?${queryString}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Utilization by hour request failed with ${res.status}`
          );
        }

        return res.json();
      })
      .then((data) => {
        setUtilizationByHourData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching utilization by hour:", err);
        setUtilizationByHourData([]);
      });

    fetch(`${API_BASE_URL}/charts/station-summary?${queryString}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Station summary request failed with ${res.status}`
          );
        }

        return res.json();
      })
      .then((data) => {
        setStationSummaryData(
          Array.isArray(data) ? data : []
        );
      })
      .catch((err) => {
        console.error("Error fetching station summary:", err);
        setStationSummaryData([]);
      });

    fetch(`${API_BASE_URL}/dashboard-summary?${queryString}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Summary request failed with ${res.status}`
          );
        }

        return res.json();
      })
      .then((data) => {
        setSummaryData({
          acUsed: data.acUsed ?? 0,
          acTotal: data.acTotal ?? 0,
          dcUsed: data.dcUsed ?? 0,
          dcTotal: data.dcTotal ?? 0
        });
      })
      .catch((err) => {
        console.error("Error fetching dashboard summary:", err);

        setSummaryData({
          acUsed: 0,
          acTotal: 0,
          dcUsed: 0,
          dcTotal: 0
        });
      });
  }, [
    chargerType,
    selectedLocations,
    startDate,
    endDate,
    grouping,
    revOnly,
    locationsLoaded
  ]);

  useEffect(() => {
    if (!quickRange) {
      return;
    }

    const today = new Date();

    let newStartDate;
    let newEndDate;

    if (quickRange === "last7") {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);

      newStartDate = formatDate(start);
      newEndDate = formatDate(today);
    } else if (quickRange === "last30") {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);

      newStartDate = formatDate(start);
      newEndDate = formatDate(today);
    } else if (quickRange === "thisMonth") {
      const start = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );

      const end = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0
      );

      newStartDate = formatDate(start);
      newEndDate = formatDate(end);
    } else if (quickRange === "lastMonth") {
      const start = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1
      );

      const end = new Date(
        today.getFullYear(),
        today.getMonth(),
        0
      );

      newStartDate = formatDate(start);
      newEndDate = formatDate(end);
    } else if (quickRange === "allTime") {
      newStartDate = "";
      newEndDate = "";
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, [quickRange]);

  return (
    <>
      <Navbar />

      <div className="dashboard-container">
        <Filters
          chargerType={chargerType}
          setChargerType={setChargerType}
          locations={filteredLocations}
          selectedLocations={selectedLocations}
          setSelectedLocations={setSelectedLocations}
          revOnly={revOnly}
          setRevOnly={setRevOnly}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          quickRange={quickRange}
          setQuickRange={setQuickRange}
          grouping={grouping}
          setGrouping={setGrouping}
          onOpenMap={() => setIsMapOpen(true)}
        />

        <SummaryBar
          startDate={startDate}
          endDate={endDate}
          acUsed={summaryData.acUsed}
          acTotal={summaryData.acTotal}
          dcUsed={summaryData.dcUsed}
          dcTotal={summaryData.dcTotal}
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
          onViewMap={() => setIsMapOpen(true)}
          locations={locations}
          selectedLocations={selectedLocations}
          chargerType={chargerType}
        />

        <ChartTabs
          chargerType={chargerType}
          grouping={grouping}
          acData={acData}
          dcData={dcData}
          acDayOfWeekData={acDayOfWeekData}
          dcDayOfWeekData={dcDayOfWeekData}
          energyBoxplotData={energyBoxplotData}
          averageEnergyData={averageEnergyData}
          timeBoxplotData={timeBoxplotData}
          averageTimeData={averageTimeData}
          acTotalEnergyData={acTotalEnergyData}
          dcTotalEnergyData={dcTotalEnergyData}
          averageEnergyByHourData={averageEnergyByHourData}
          utilizationByHourData={utilizationByHourData}
          stationSummaryData={stationSummaryData}
        />
      </div>

      <Footer />

      <ChargerMap
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        locations={locations}
        selectedLocations={selectedLocations}
        chargerType={chargerType}
      />
    </>
  );
}

export default Dashboard;